import {
  DeleteSpecObjectMappingResponse,
  UpdateSpecObjectMappingResponse,
  SpecObjectMappingRecord,
  BatchGetSpecsResponse,
  BatchGetSpecObjectMappingsResponse,
  Spec,
  SpecObjectConfigRecord,
  SpecObjectMappingId,
  BatchGetSpecObjectConfigsResponse,
  SpecConfig,
  BatchDeleteSpecObjectMappingsResponse,
  BatchGetSpecConfigsResponse,
} from '@fullstory/proto/fs/services/search-settings';
import { SpecActions } from './actions';
import { RequestActionType, combineReducers, createRequestReducer } from '@fullstory/redux-util';
import { SpecRequestTypes } from './types';

type SpecsState = {
  specs: Spec[];
  specConfigs: SpecConfig[];
  specObjectMappingRecords: SpecObjectMappingRecord[];
  specObjectConfigRecords: SpecObjectConfigRecord[];
};

const specMappingRecordToId = (
  specMappingRecord: SpecObjectMappingRecord,
): SpecObjectMappingId | null =>
  specMappingRecord?.mapping
    ? {
        objectSlug: specMappingRecord.mapping?.objectSlug,
        targetObjectId: specMappingRecord.mapping.targetObjectId,
        targetObjectType: specMappingRecord.mapping.targetObjectType,
      }
    : null;

const matchSpecMappingToId = (
  specMappingRecord: SpecObjectMappingRecord,
  id: SpecObjectMappingId,
) =>
  specMappingRecord.mapping?.objectSlug == id.objectSlug &&
  specMappingRecord.mapping.targetObjectId == id.targetObjectId &&
  specMappingRecord.mapping.targetObjectType == id.targetObjectType;

const updateSpecMappingRecord = (state: SpecsState, mapping: SpecObjectMappingRecord) => {
  const specObjectMappingId = specMappingRecordToId(mapping);
  if (!specObjectMappingId) {
    return state; // shouldn't be possible since we just mapped, but if it is, return original state with no changes
  }

  const index = state.specObjectMappingRecords.findIndex(existingMapping =>
    matchSpecMappingToId(existingMapping, specObjectMappingId),
  );
  if (index === -1) {
    return {
      ...state,
      specObjectMappingRecords: [...state.specObjectMappingRecords, mapping],
    };
  } else {
    const newMappings = [...state.specObjectMappingRecords];
    newMappings[index] = mapping;
    return { ...state, specObjectMappingRecords: newMappings };
  }
};

export const specs = (
  state: SpecsState = {
    specs: [],
    specObjectMappingRecords: [],
    specObjectConfigRecords: [],
    specConfigs: [],
  },
  action: RequestActionType<typeof SpecActions>,
): SpecsState => {
  switch (action.type) {
    case SpecRequestTypes.ListSpecsSuccess: {
      const { specs } = action.data;
      return { ...state, specs: Object.values(specs) };
    }
    case SpecRequestTypes.ListSpecConfigsSuccess: {
      const { specConfigs } = action.data;
      return { ...state, specConfigs: Object.values(specConfigs) };
    }
    case SpecRequestTypes.UpdateSpecConfigSuccess: {
      const { specConfig } = action.data;

      if (!specConfig) return state;

      const index = state.specConfigs.findIndex(
        existingConfig => existingConfig?.specSlug == specConfig.specSlug,
      );

      if (index === -1) {
        return {
          ...state,
          specConfigs: [...state.specConfigs, specConfig],
        };
      } else {
        const newConfigs = [...state.specConfigs];
        newConfigs[index] = specConfig;
        return { ...state, specConfigs: newConfigs };
      }
    }
    case SpecRequestTypes.ListSpecMappingsSuccess: {
      const { mappings } = action.data;
      return { ...state, specObjectMappingRecords: Object.values(mappings) };
    }
    case SpecRequestTypes.UpdateSpecMappingsSuccess: {
      const { mapping: updatedMapping } = action.data as UpdateSpecObjectMappingResponse;
      if (!updatedMapping) return state;
      return updateSpecMappingRecord(state, updatedMapping);
    }
    case SpecRequestTypes.BatchUpdateSpecMappingsSuccess: {
      let updatedState = state;
      action.data.mappings.forEach(mapping => {
        updatedState = updateSpecMappingRecord(updatedState, mapping);
      });
      return updatedState;
    }
    case SpecRequestTypes.DeleteSpecMappingsSuccess: {
      const { mappingId } = action.data as DeleteSpecObjectMappingResponse;
      if (mappingId) {
        const mappings = state.specObjectMappingRecords.filter(
          mapping => !matchSpecMappingToId(mapping, mappingId),
        );
        return { ...state, specObjectMappingRecords: mappings };
      }
      return { ...state };
    }
    case SpecRequestTypes.BatchDeleteSpecObjectMappingsSuccess: {
      const { mappingIds } = action.data as BatchDeleteSpecObjectMappingsResponse;
      const mappings = state.specObjectMappingRecords.filter(
        mapping =>
          !mappingIds.some(deletedMappingId => matchSpecMappingToId(mapping, deletedMappingId)),
      );
      return { ...state, specObjectMappingRecords: mappings };
    }
    case SpecRequestTypes.ListSpecObjectConfigsSuccess: {
      const { configs } = action.data;
      return { ...state, specObjectConfigRecords: Object.values(configs) };
    }
    case SpecRequestTypes.UpdateSpecObjectConfigSuccess: {
      const { config: updatedConfig } = action.data;

      if (!updatedConfig) return state;

      const index = state.specObjectConfigRecords.findIndex(
        existingConfig => existingConfig.config?.objectSlug == updatedConfig.config?.objectSlug,
      );

      if (index === -1) {
        return {
          ...state,
          specObjectConfigRecords: [...state.specObjectConfigRecords, updatedConfig],
        };
      } else {
        const newConfigs = [...state.specObjectConfigRecords];
        newConfigs[index] = updatedConfig;
        return { ...state, specObjectConfigRecords: newConfigs };
      }
    }
  }
  return state;
};

const specsRequest = createRequestReducer<BatchGetSpecsResponse>()(
  SpecRequestTypes.ListSpecsRequest,
  SpecRequestTypes.ListSpecsSuccess,
  SpecRequestTypes.ListSpecsFailure,
);

const specConfigsRequest = createRequestReducer<BatchGetSpecConfigsResponse>()(
  SpecRequestTypes.ListSpecConfigsRequest,
  SpecRequestTypes.ListSpecConfigsSuccess,
  SpecRequestTypes.ListSpecConfigsFailure,
);

const specObjectConfigsRequest = createRequestReducer<BatchGetSpecObjectConfigsResponse>()(
  SpecRequestTypes.ListSpecObjectConfigsRequest,
  SpecRequestTypes.ListSpecObjectConfigsSuccess,
  SpecRequestTypes.ListSpecObjectConfigsFailure,
);

const specMappingsRequest = createRequestReducer<BatchGetSpecObjectMappingsResponse>()(
  SpecRequestTypes.ListSpecMappingsRequest,
  SpecRequestTypes.ListSpecMappingsSuccess,
  SpecRequestTypes.ListSpecMappingsFailure,
);

type SpecsStateState = {
  specs: ReturnType<typeof specs>;
  specsRequest: ReturnType<typeof specsRequest>;
  specConfigsRequest: ReturnType<typeof specConfigsRequest>;
  specObjectConfigsRequest: ReturnType<typeof specObjectConfigsRequest>;
  specMappingsRequest: ReturnType<typeof specMappingsRequest>;
};

export const specsReducer = combineReducers<SpecsStateState>({
  specs,
  specsRequest,
  specConfigsRequest,
  specObjectConfigsRequest,
  specMappingsRequest,
});
